import React, { useEffect, useMemo, useState, useRef } from "react";
import { Row, Col, Card, Container } from "react-bootstrap";
import { Bubble, Line, Bar } from "react-chartjs-2";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend as RechartsLegend } from "recharts";
import { motion, AnimatePresence } from "framer-motion";

import { fetchSecurityOverview, fetchSecurityThreatSources, fetchSocAvailableDates, fetchEndpointDetails, fetchEndpointAvailableDates, fetchClients } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";
import OverviewCard from "../../components/OverviewCard";
import EndpointDetailsChart from "../../components/charts/EndpointDetailsChart";

import { FaExclamationTriangle, FaShieldAlt, FaServer, FaClock, FaNetworkWired, FaSpinner, FaChartLine, FaSlidersH, FaCheck, FaCodiepie, FaChartBar, FaUsers, FaCalendarAlt, FaTimes, FaChevronLeft, FaChevronRight, FaExpand, FaCompress, FaList } from "react-icons/fa";
import SecurityAnalyticsModal from "./SecurityAnalyticsModal";

const fadeUp = {
  initial: { opacity: 0, y: 25 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.12 } },
};

const SECURITY_CHART_MIN_HEIGHT = "55vh";

const TREND_LINE_DASH_PATTERNS = [
  [],
  [6, 4],
  [2, 3],
  [8, 4, 2, 4],
  [2, 4],
  [2, 3],
  [10, 5],
];

const TREND_LINE_DASH_BY_LABEL = {
  SentinelOne: [],
  "KnowBe4 Licensed": [6, 4],
  "KnowBe4 Licenced Seats": [6, 4],
  "KnowBe4 Used": [2, 4],
  "KnowBe4 Used Seats": [2, 4],
  "Rapid7 IDR": [2, 3],
  "Rapid7 IVM": [8, 4, 2, 4],
  "Rapid7 iVM": [8, 4, 2, 4],
  "DarkWeb ID": [2, 3],
  "DarkWeb ID Domains Being Searched": [2, 3],
  CrowdStrike: [10, 5],
};

const getTrendLineDash = (label, index = 0) =>
  TREND_LINE_DASH_BY_LABEL[label] ?? TREND_LINE_DASH_PATTERNS[index % TREND_LINE_DASH_PATTERNS.length];

const TrendInlineLegend = ({ items }) => {
  if (!items || items.length === 0) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
        gap: "10px 14px",
        padding: "6px 0 10px",
        borderBottom: "1px solid rgba(148,163,184,0.22)",
        marginBottom: 4,
      }}
    >
      {items.map((item, index) => {
        const dash = getTrendLineDash(item.label, index);
        const isActive = item.active !== false;
        return (
          <div
            key={item.id}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 7,
              opacity: isActive ? 1 : 0.4,
              minWidth: 0,
            }}
          >
            <svg width="30" height="10" aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }}>
              <line
                x1="0"
                y1="5"
                x2="30"
                y2="5"
                stroke={item.color || "#667eea"}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={dash.length ? dash.join(" ") : undefined}
              />
            </svg>
            <span
              style={{
                fontSize: 11,
                color: "#6b7280",
                fontWeight: 400,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const HeadingIcon = ({ icon: Icon, color, bg }) => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: 10,
      background: bg || "rgba(111,66,193,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Icon size={16} color={color || "var(--color-primary)"} />
  </div>
);

/** Small reusable Legend dropdown (no hooks inside) */
const LegendDropdown = ({ items, open, onToggleOpen, onToggleItem, icon: Icon = FaSlidersH }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="position-relative">
      <style>{`
        .legend-dropdown-btn { background: var(--color-primary-light); }
        .legend-dropdown-btn:hover { background: rgba(111,66,193,0.25); }
      `}</style>
      <button
        type="button"
        onClick={onToggleOpen}
        className="legend-dropdown-btn"
        style={{
          borderRadius: 999,
          border: "1px solid var(--color-border)",
          color: "var(--color-text)",
          padding: "4px 10px",
          fontSize: 11,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          boxShadow: "var(--shadow-sm)",
          transition: "all 0.2s ease",
          whiteSpace: "nowrap",
        }}
      >
        <Icon size={10} />
        Legend
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "110%",
            right: 0,
            minWidth: 200,
            background: "var(--color-bg-card)",
            borderRadius: 14,
            border: "1px solid var(--color-border)",
            boxShadow: "var(--shadow-md)",
            padding: "8px 0",
            zIndex: 20,
          }}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onToggleItem(item.id)}
              style={{
                width: "100%",
                border: "none",
                background: "transparent",
                padding: "6px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                cursor: "pointer",
                fontSize: 12,
                color: item.active ? "var(--color-text)" : "var(--color-text-light)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: item.color || "var(--color-primary)",
                  }}
                />
                <span>{item.label}</span>
              </div>

              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 6,
                  border: `1px solid ${item.active ? "var(--color-primary)" : "var(--color-border)"}`,
                  background: item.active ? "var(--color-primary-light)" : "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.active && <FaCheck size={10} color="var(--color-primary)" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EndpointDistributionTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "#1a1a2e",
          padding: "8px 12px",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            backgroundColor: entry.fill,
            borderRadius: 2,
            flexShrink: 0,
          }}
        />
        <span style={{ color: "#ffffff", fontSize: 12, fontWeight: 500 }}>
          {entry.name}: {entry.value}
        </span>
      </div>
    );
  }
  return null;
};

const renderEndpointLegend = (props) => {
  const { payload } = props;
  const isDark = typeof document !== "undefined" && document.body.classList.contains("dark-mode");
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: "6px 16px",
        paddingTop: 0,
        marginTop: -10,
      }}
    >
      {payload.map((entry, index) => (
        <div key={`ep-legend-${index}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 11, fontWeight: 400, color: isDark ? "#a0a4b8" : "#6c737f" }}>
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const SecurityOverview = ({ clientId, selectedClient }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [clientName, setClientName] = useState("");
  // Chart.js renders to canvas, so it can't resolve CSS custom properties
  // like var(--color-text) and needs a real hex value instead — reading
  // document.body.classList synchronously during render can catch a stale
  // value in the brief window before Layout's dark-mode effect updates it,
  // so track it reactively via a MutationObserver instead.
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains("dark-mode"));

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("dark-mode"));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Fetch client name - use selectedClient if available, otherwise fetch and cache
  useEffect(() => {
    if (!clientId) {
      setClientName("");
      return;
    }

    // First, check if selectedClient prop has the name
    if (selectedClient?.client_name) {
      setClientName(selectedClient.client_name);
      return;
    }

    // Check cache
    const cacheKey = `client_name_${clientId}`;
    const cachedName = getCachedData(cacheKey);
    if (cachedName) {
      setClientName(cachedName);
      return;
    }

    // Fetch if not in cache
    const fetchClientName = async () => {
      try {
        const clientsList = await fetchClients();
        const foundClient = clientsList.find(c => c.client_id === parseInt(clientId));
        if (foundClient?.client_name) {
          setClientName(foundClient.client_name);
          // Cache the client name
          setCachedData(cacheKey, foundClient.client_name);
        } else {
          setClientName("");
        }
      } catch (error) {
        console.error("Error fetching client name:", error);
        setClientName("");
      }
    };

    fetchClientName();
  }, [clientId, selectedClient, getCachedData, setCachedData]);

  const [data, setData] = useState(null);
  const [threatSources, setThreatSources] = useState([]);
  const [loading, setLoading] = useState(false);

  const dataLoadedRef = useRef(new Set());
  const currentClientRef = useRef(clientId);
  const isMock = process.env.REACT_APP_USE_HAR_MOCK === "true";

  // Legend dropdown & visibility state
  const [endpointLegendOpen, setEndpointLegendOpen] = useState(false);
  const [trendLegendOpen, setTrendLegendOpen] = useState(false);
  const [bubbleLegendOpen, setBubbleLegendOpen] = useState(false);

  const [endpointVisible, setEndpointVisible] = useState({});
  const [trendVisible, setTrendVisible] = useState({});
  const [bubbleVisible, setBubbleVisible] = useState({});

  // Computed directly from data/endpointVisible (rather than the later-derived
  // rechartsEndpointData) so this can be memoized above the component's early
  // returns — a hook can't be called conditionally after them.
  const endpointDistributionTotal = useMemo(() => {
    const labels = data?.endpointDistribution?.labels || [];
    const values = data?.endpointDistribution?.values || [];
    return labels.reduce((sum, label, idx) => {
      if (endpointVisible[label] === false) return sum;
      return sum + (Number(values[idx]) || 0);
    }, 0);
  }, [data, endpointVisible]);

  // Month filter state for SOC Endpoints Distribution (monthly data)
  const [socSelectedMonth, setSocSelectedMonth] = useState(null);
  const [socShowPicker, setSocShowPicker] = useState(false);
  const socPickerRef = useRef(null);
  const [socLoading, setSocLoading] = useState(false);
  const [socAvailableDates, setSocAvailableDates] = useState([]);

  // Month filter state for Endpoint Details (monthly data)
  const [detailsSelectedMonth, setDetailsSelectedMonth] = useState(null);
  const [detailsShowPicker, setDetailsShowPicker] = useState(false);
  const detailsPickerRef = useRef(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsAvailableDates, setDetailsAvailableDates] = useState([]);
  const [detailsData, setDetailsData] = useState(null);
  const [momFullscreen, setMomFullscreen] = useState(false);

  // Analytics panel state
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  // Month filter state for Monthly Endpoint Trends
  const [trendSelectedMonth, setTrendSelectedMonth] = useState(null);
  const [trendShowPicker, setTrendShowPicker] = useState(false);
  const trendPickerRef = useRef(null);

  // Format date for display
  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // Handle click outside picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (socPickerRef.current && !socPickerRef.current.contains(event.target)) {
        setSocShowPicker(false);
      }
      if (detailsPickerRef.current && !detailsPickerRef.current.contains(event.target)) {
        setDetailsShowPicker(false);
      }
      if (trendPickerRef.current && !trendPickerRef.current.contains(event.target)) {
        setTrendShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reusable Calendar Component
  const CalendarPopup = ({ selectedDate, setSelectedDate, setShowCalendar, availableDates = [] }) => {
    const [currentMonth, setCurrentMonth] = useState(selectedDate ? new Date(selectedDate) : new Date());
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    
    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const startingDay = firstDay.getDay();
      const days = [];
      for (let i = 0; i < startingDay; i++) days.push(null);
      for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
      return days;
    };

    const isToday = (date) => date && date.toDateString() === new Date().toDateString();
    const isSelected = (date) => date && selectedDate && date.toDateString() === new Date(selectedDate).toDateString();
    const isFuture = (date) => { if (!date) return false; const today = new Date(); today.setHours(0,0,0,0); return date > today; };
    
    // Check if the date's month has data available
    const hasDataForMonth = (date) => {
      if (!date || !availableDates || availableDates.length === 0) return false;
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // JS months are 0-indexed
      return availableDates.some(d => d.year === year && d.month === month);
    };

    const handleDateSelect = (date) => { if (date && !isFuture(date)) { setSelectedDate(date); setShowCalendar(false); } };
    const goToPrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    const goToNextMonth = () => { const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1); if (next <= new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)) setCurrentMonth(next); };

    return (
      <motion.div initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.95 }} transition={{ duration: 0.15 }}
        style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "var(--color-bg-card)", borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,0.15)", border: "1px solid var(--color-border)", zIndex: 1000, minWidth: 220, padding: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, padding: "0 4px" }}>
          <button onClick={goToPrevMonth} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "var(--color-text)", display: "flex", alignItems: "center", justifyContent: "center" }}><FaChevronLeft size={10} /></button>
          <span style={{ fontWeight: 600, color: "var(--color-text)", fontSize: 12 }}>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
          <button onClick={goToNextMonth} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "var(--color-text)", display: "flex", alignItems: "center", justifyContent: "center" }}><FaChevronRight size={10} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 4 }}>
          {dayNames.map((day) => (<div key={day} style={{ textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-light)", padding: 2 }}>{day}</div>))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
          {getDaysInMonth(currentMonth).map((date, index) => {
            const hasData = hasDataForMonth(date);
            return (
              <div key={index} onClick={() => handleDateSelect(date)}
                style={{ width: 26, height: 26, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontSize: 10, borderRadius: 6, cursor: date && !isFuture(date) ? "pointer" : "default",
                  background: isSelected(date) ? "#8b5cf6" : isToday(date) ? "rgba(139, 92, 246, 0.15)" : "transparent",
                  color: isSelected(date) ? "#ffffff" : isFuture(date) ? "var(--color-text-light)" : isToday(date) ? "#8b5cf6" : "var(--color-text)",
                  opacity: date ? (isFuture(date) ? 0.4 : 1) : 0, fontWeight: isToday(date) || isSelected(date) ? 600 : 400, transition: "all 0.15s ease", position: "relative" }}
                onMouseEnter={(e) => { if (date && !isFuture(date) && !isSelected(date)) e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)"; }}
                onMouseLeave={(e) => { if (date && !isFuture(date) && !isSelected(date)) e.currentTarget.style.background = isToday(date) ? "rgba(139, 92, 246, 0.15)" : "transparent"; }}>
                {date ? date.getDate() : ""}
                {date && hasData && !isSelected(date) && (
                  <div style={{ position: "absolute", bottom: 2, width: 4, height: 4, borderRadius: "50%", background: "#22c55e" }} />
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--color-border)" }}>
          <button onClick={() => { setSelectedDate(null); setShowCalendar(false); }} style={{ background: "transparent", border: "none", color: "#8b5cf6", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "4px 8px", borderRadius: 6 }}>Clear</button>
          <button onClick={() => { setSelectedDate(new Date()); setShowCalendar(false); }} style={{ background: "#8b5cf6", border: "none", color: "#ffffff", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "4px 10px", borderRadius: 6, marginLeft: 6 }}>Today</button>
        </div>
      </motion.div>
    );
  };

  // Reusable Date Badge Component
  const DateBadge = ({ selectedDate, setSelectedDate, showCalendar, setShowCalendar, calendarRef, availableDates = [] }) => (
    <div className="position-relative" ref={calendarRef}>
      <div
        onClick={() => setShowCalendar(!showCalendar)}
        style={{
          borderRadius: 10,
          background: selectedDate ? "#ffffff" : "var(--color-bg-card)",
          border: selectedDate ? "1px solid rgba(139, 92, 246, 0.3)" : "1px solid var(--color-border)",
          padding: "6px 12px",
          fontSize: 12,
          cursor: "pointer",
          minWidth: 120,
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = selectedDate ? "rgba(139, 92, 246, 0.3)" : "var(--color-border)"; }}
      >
        <FaCalendarAlt size={11} color="#8b5cf6" />
        <span style={{ color: selectedDate ? "#8b5cf6" : "var(--color-text)", fontWeight: 500, flex: 1 }}>
          {selectedDate ? formatDateForDisplay(selectedDate) : "Select Date"}
        </span>
        {selectedDate ? (
          <FaTimes size={10} color="#8b5cf6" onClick={(e) => { e.stopPropagation(); setSelectedDate(null); setShowCalendar(false); }} style={{ cursor: "pointer" }} />
        ) : (
          <FaChevronRight size={10} style={{ color: "var(--color-text-light)", transform: showCalendar ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
        )}
      </div>
      <AnimatePresence>{showCalendar && <CalendarPopup selectedDate={selectedDate} setSelectedDate={setSelectedDate} setShowCalendar={setShowCalendar} availableDates={availableDates} />}</AnimatePresence>
    </div>
  );

  // Month Picker Component (for monthly data charts like SOC & Endpoint Details)
  const MonthPickerPopup = ({ selectedMonth, setSelectedMonth, setShowPicker, availableDates = [] }) => {
    const currentDate = new Date();
    const [currentYear, setCurrentYear] = useState(selectedMonth ? selectedMonth.year : currentDate.getFullYear());
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    const isCurrentMonth = (month) => currentDate.getFullYear() === currentYear && currentDate.getMonth() === month;
    const isSelected = (month) => selectedMonth && selectedMonth.year === currentYear && selectedMonth.month === month + 1;
    const isFuture = (month) => currentYear > currentDate.getFullYear() || (currentYear === currentDate.getFullYear() && month > currentDate.getMonth());
    
    const hasData = (month) => {
      if (!availableDates || availableDates.length === 0) return false;
      return availableDates.some(d => d.year === currentYear && d.month === month + 1);
    };

    const handleMonthSelect = (month) => {
      if (!isFuture(month)) {
        setSelectedMonth({ year: currentYear, month: month + 1 });
        setShowPicker(false);
      }
    };

    const goToPrevYear = () => setCurrentYear(currentYear - 1);
    const goToNextYear = () => { if (currentYear < currentDate.getFullYear()) setCurrentYear(currentYear + 1); };

    return (
      <motion.div initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.95 }} transition={{ duration: 0.15 }}
        style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "var(--color-bg-card)", borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,0.15)", border: "1px solid var(--color-border)", zIndex: 1000, minWidth: 200, padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, padding: "0 4px" }}>
          <button onClick={goToPrevYear} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "var(--color-text)", display: "flex", alignItems: "center", justifyContent: "center" }}><FaChevronLeft size={10} /></button>
          <span style={{ fontWeight: 600, color: "var(--color-text)", fontSize: 14 }}>{currentYear}</span>
          <button onClick={goToNextYear} disabled={currentYear >= currentDate.getFullYear()} style={{ background: "transparent", border: "none", cursor: currentYear >= currentDate.getFullYear() ? "default" : "pointer", padding: 4, borderRadius: 6, color: currentYear >= currentDate.getFullYear() ? "var(--color-text-light)" : "var(--color-text)", display: "flex", alignItems: "center", justifyContent: "center", opacity: currentYear >= currentDate.getFullYear() ? 0.4 : 1 }}><FaChevronRight size={10} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
          {monthNames.map((month, index) => {
            const monthHasData = hasData(index);
            return (
              <div key={month} onClick={() => handleMonthSelect(index)}
                style={{
                  padding: "8px 4px",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: isSelected(index) || isCurrentMonth(index) ? 600 : 400,
                  borderRadius: 8,
                  cursor: isFuture(index) ? "default" : "pointer",
                  background: isSelected(index) ? "#8b5cf6" : isCurrentMonth(index) ? "rgba(139, 92, 246, 0.15)" : "transparent",
                  color: isSelected(index) ? "#ffffff" : isFuture(index) ? "var(--color-text-light)" : isCurrentMonth(index) ? "#8b5cf6" : "var(--color-text)",
                  opacity: isFuture(index) ? 0.4 : 1,
                  transition: "all 0.15s ease",
                  position: "relative",
                  border: monthHasData && !isSelected(index) ? "1px solid #22c55e" : "1px solid transparent",
                }}
                onMouseEnter={(e) => { if (!isFuture(index) && !isSelected(index)) e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)"; }}
                onMouseLeave={(e) => { if (!isFuture(index) && !isSelected(index)) e.currentTarget.style.background = isCurrentMonth(index) ? "rgba(139, 92, 246, 0.15)" : "transparent"; }}>
                {month}
                {monthHasData && !isSelected(index) && (
                  <div style={{ position: "absolute", top: 3, right: 3, width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} />
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--color-border)" }}>
          <button onClick={() => { setSelectedMonth(null); setShowPicker(false); }} style={{ background: "transparent", border: "none", color: "#8b5cf6", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "4px 8px", borderRadius: 6 }}>Clear</button>
          <button onClick={() => { setSelectedMonth({ year: currentDate.getFullYear(), month: currentDate.getMonth() + 1 }); setShowPicker(false); }} style={{ background: "#8b5cf6", border: "none", color: "#ffffff", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "4px 10px", borderRadius: 6, marginLeft: 6 }}>This Month</button>
        </div>
      </motion.div>
    );
  };

  // Month Badge Component (for monthly data charts)
  const MonthBadge = ({ selectedMonth, setSelectedMonth, showPicker, setShowPicker, pickerRef, availableDates = [] }) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const formatMonth = (month) => month ? `${monthNames[month.month - 1]} ${month.year}` : "";

    return (
      <div className="position-relative" ref={pickerRef}>
        <div
          onClick={() => setShowPicker(!showPicker)}
          style={{
            borderRadius: 8,
            background: selectedMonth ? "#ffffff" : "var(--color-bg-card)",
            border: selectedMonth ? "1px solid rgba(139, 92, 246, 0.3)" : "1px solid var(--color-border)",
            padding: "4px 10px",
            fontSize: 11,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = selectedMonth ? "rgba(139, 92, 246, 0.3)" : "var(--color-border)"; }}
        >
          <FaCalendarAlt size={10} color="#8b5cf6" />
          <span style={{ color: selectedMonth ? "#8b5cf6" : "var(--color-text)", fontWeight: 500 }}>
            {selectedMonth ? formatMonth(selectedMonth) : "Select Month"}
          </span>
          {selectedMonth ? (
            <FaTimes size={10} color="#8b5cf6" onClick={(e) => { e.stopPropagation(); setSelectedMonth(null); setShowPicker(false); }} style={{ cursor: "pointer" }} />
          ) : (
            <FaChevronRight size={10} style={{ color: "var(--color-text-light)", transform: showPicker ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
          )}
        </div>
        <AnimatePresence>{showPicker && <MonthPickerPopup selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} setShowPicker={setShowPicker} availableDates={availableDates} />}</AnimatePresence>
      </div>
    );
  };

  /* -------------------------------------------------------------------------- */
  /*                              DATA LOAD EFFECT                              */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    // Reset tracking if client changes
    if (currentClientRef.current !== clientId) {
      dataLoadedRef.current.clear();
      currentClientRef.current = clientId;
    }

    // If no client & not in mock mode
    if (!clientId && !isMock) {
      setData(null);
      setThreatSources([]);
      setLoading(false);
      return;
    }

    const cacheKey = `security_${clientId}`;
    if (dataLoadedRef.current.has(cacheKey)) {
      return;
    }

    const cachedData = getCachedData("security_overview", clientId);
    if (cachedData !== null && cachedData !== undefined) {
      setData(cachedData);
      setLoading(false);
      dataLoadedRef.current.add(cacheKey);

      const cachedThreatSources = getCachedData("security_threat_sources", clientId);
      if (cachedThreatSources) {
        setThreatSources(Array.isArray(cachedThreatSources) ? cachedThreatSources : []);
      } else {
        fetchSecurityThreatSources(clientId)
          .then((res) => {
            const result = Array.isArray(res) ? res : [];
            setCachedData("security_threat_sources", result, clientId);
            setThreatSources(result);
          })
          .catch((error) => {
            console.error("Error fetching threat sources:", error);
            setThreatSources([]);
          });
      }

      return;
    }

    setLoading(true);
    dataLoadedRef.current.add(cacheKey);

    fetchSecurityOverview(clientId)
      .then((result) => {
        if (result === null) {
          setData(null);
          setCachedData("security_overview", null, clientId);
        } else {
          setCachedData("security_overview", result, clientId);
          setData(result);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching security overview:", error);
        setData(null);
        setCachedData("security_overview", null, clientId);
        setLoading(false);
        dataLoadedRef.current.delete(cacheKey);
      });

    const cachedThreatSources = getCachedData("security_threat_sources", clientId);
    if (cachedThreatSources) {
      setThreatSources(Array.isArray(cachedThreatSources) ? cachedThreatSources : []);
    } else {
      fetchSecurityThreatSources(clientId)
        .then((res) => {
          const result = Array.isArray(res) ? res : [];
          setCachedData("security_threat_sources", result, clientId);
          setThreatSources(result);
        })
        .catch((error) => {
          console.error("Error fetching threat sources:", error);
          setThreatSources([]);
        });
    }
  }, [clientId, getCachedData, setCachedData, isMock]);

  // Refetch SOC threat sources when month changes
  useEffect(() => {
    if (!clientId && !isMock) return;
    
    setSocLoading(true);
    // Convert month object to date string for API (first day of month)
    const selectedDate = socSelectedMonth ? new Date(socSelectedMonth.year, socSelectedMonth.month - 1, 1) : null;
    fetchSecurityThreatSources(clientId, selectedDate)
      .then((res) => {
        const result = Array.isArray(res) ? res : [];
        setThreatSources(result);
        setSocLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching threat sources:", error);
        setThreatSources([]);
        setSocLoading(false);
      });
  }, [socSelectedMonth, clientId, isMock]);

  // Fetch SOC available dates
  useEffect(() => {
    if (!clientId && !isMock) return;
    
    fetchSocAvailableDates(clientId)
      .then((dates) => {
        setSocAvailableDates(dates || []);
      })
      .catch((error) => {
        console.error("Error fetching SOC available dates:", error);
        setSocAvailableDates([]);
      });
  }, [clientId, isMock]);

  // Refetch endpoint details ONLY when a specific month is selected
  // (default view uses data.endpointDetails from fetchSecurityOverview, which is already SOC-filtered)
  useEffect(() => {
    if (!clientId && !isMock) return;

    // Only call the API when a month is explicitly selected
    if (!detailsSelectedMonth) {
      setDetailsData(null);
      setDetailsLoading(false);
      return;
    }

    setDetailsLoading(true);
    // Convert month object to date string for API (first day of month)
    const selectedDate = new Date(detailsSelectedMonth.year, detailsSelectedMonth.month - 1, 1);
    fetchEndpointDetails(clientId, selectedDate)
      .then((result) => {
        setDetailsData(result);
        setDetailsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching endpoint details:", error);
        setDetailsData(null);
        setDetailsLoading(false);
      });
  }, [detailsSelectedMonth, clientId, isMock]);

  // Fetch endpoint available dates
  useEffect(() => {
    if (!clientId && !isMock) return;
    
    fetchEndpointAvailableDates(clientId)
      .then((dates) => {
        setDetailsAvailableDates(dates || []);
      })
      .catch((error) => {
        console.error("Error fetching endpoint available dates:", error);
        setDetailsAvailableDates([]);
      });
  }, [clientId, isMock]);

  /* -------------------------------------------------------------------------- */
  /*                               HELPER FLAGS                                 */
  /* -------------------------------------------------------------------------- */

  const hasNoData = (() => {
    if (!data) return false;

    const hasSummary =
      data.summary && (data.summary.totalSocEndpoints > 0 || (data.summary.uniqueEndpointTypes > 0 && data.summary.uniqueEndpointTypes !== null && data.summary.uniqueEndpointTypes !== undefined));

    const hasDistribution = data.endpointDistribution && data.endpointDistribution.values && data.endpointDistribution.values.length > 0 && data.endpointDistribution.values.some((v) => v > 0);

    const hasAlerts = data.alertsTable && Array.isArray(data.alertsTable) && data.alertsTable.length > 0;

    const hasTrend =
      data.monthlyEndpointTrend &&
      data.monthlyEndpointTrend.endpoints &&
      Object.keys(data.monthlyEndpointTrend.endpoints).length > 0 &&
      Object.values(data.monthlyEndpointTrend.endpoints).some((arr) => Array.isArray(arr) && arr.length > 0 && arr.some((v) => v > 0));

    return !hasSummary && !hasDistribution && !hasAlerts && !hasTrend;
  })();

  /* -------------------------------------------------------------------------- */
  /*                          EARLY UI STATES (SAFE)                            */
  /*   (These returns come AFTER all hooks, so hooks order is always stable)    */
  /* -------------------------------------------------------------------------- */

  if (!clientId && !isMock) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(114,105,239,0.1) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid var(--color-primary-light)",
                  }}
                >
                  <FaShieldAlt size={48} color="var(--color-primary)" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  Select a Client
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                  }}
                >
                  Please select a client from the search bar above to view their security overview data.
                </motion.p>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(114,105,239,0.1) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid var(--color-primary-light)",
                  }}
                >
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                    <FaSpinner size={48} color="var(--color-primary)" />
                  </motion.div>
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  Preparing Security Overview
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                    marginBottom: 32,
                  }}
                >
                  Preparing security overview data for selected client...
                </motion.p>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  if (!data || hasNoData) {
    const clientName = selectedClient?.client_name || "selected client";
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(108,117,125,0.1) 0%, rgba(108,117,125,0.05) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid rgba(108,117,125,0.15)",
                  }}
                >
                  <FaChartLine size={48} color="var(--color-text-light)" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  No Security Data Available
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                    marginBottom: 32,
                  }}
                >
                  There is no security data available for selected client <strong>{clientName}</strong>.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  style={{
                    padding: "16px 24px",
                    borderRadius: 12,
                    background: "rgba(108,117,125,0.08)",
                    border: "1px solid rgba(108,117,125,0.15)",
                    fontSize: 14,
                    color: "var(--color-text-light)",
                    maxWidth: 400,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FaExclamationTriangle size={16} />
                    <span>Security data will appear here once the client has security endpoints or alerts recorded.</span>
                  </div>
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                          CHART DATA + LEGEND ITEMS                         */
  /* -------------------------------------------------------------------------- */

  // Endpoint Distribution (Doughnut)
  const endpointLabels = data.endpointDistribution?.labels || [];
  const endpointValues = data.endpointDistribution?.values || [];
  const endpointColors = data.endpointDistribution?.colors || [];

  const endpointLegendItems = endpointLabels.map((label, idx) => ({
    id: label,
    label,
    color: endpointColors[idx] || "var(--color-primary)",
    active: endpointVisible[label] !== false,
  }));

  const endpointActiveIndices = endpointLabels.map((label, idx) => (endpointVisible[label] === false ? null : idx)).filter((idx) => idx !== null);

  const endpointData = {
    labels: endpointActiveIndices.map((i) => endpointLabels[i]),
    datasets: [
      {
        data: endpointActiveIndices.map((i) => endpointValues[i]),
        backgroundColor: endpointActiveIndices.map((i) => endpointColors[i] || "var(--color-primary)"),
        borderWidth: 0,
      },
    ],
  };

  const hasEndpointDistributionData = endpointData.labels.length > 0 && endpointData.datasets?.[0]?.data?.some((v) => v > 0);

  const rechartsEndpointData = endpointData.labels.map((label, idx) => ({
    name: label,
    value: endpointData.datasets[0].data[idx],
    fill: endpointData.datasets[0].backgroundColor[idx],
  }));

  const toggleEndpointLabel = (id) => {
    setEndpointVisible((prev) => ({
      ...prev,
      [id]: prev[id] === false ? true : false,
    }));
  };


  const trendEndpoints = data.monthlyEndpointTrend?.endpoints && Object.keys(data.monthlyEndpointTrend.endpoints);

  const trendLegendItems =
    trendEndpoints?.map((endpointKey) => ({
      id: endpointKey,
      label: endpointKey,
      color: data.monthlyEndpointTrend.colors?.[endpointKey] || "#667eea",
      active: trendVisible[endpointKey] !== false,
    })) || [];

  // Filter trend data based on selected month (always limited to last 6 months)
  const getFilteredTrendData = () => {
    const labels = data.monthlyEndpointTrend?.labels || [];
    const endpoints = data.monthlyEndpointTrend?.endpoints || {};
    
    if (labels.length === 0) {
      return { labels, endpoints };
    }
    
    // Determine the end index
    let endIndex = labels.length;
    if (trendSelectedMonth) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const selectedLabel = `${monthNames[trendSelectedMonth.month - 1]} ${trendSelectedMonth.year}`;
      const selectedIndex = labels.indexOf(selectedLabel);
      if (selectedIndex !== -1) {
        endIndex = selectedIndex + 1;
      }
    }
    
    // Always limit to last 6 months
    const startIndex = Math.max(0, endIndex - 6);
    
    const filteredLabels = labels.slice(startIndex, endIndex);
    const filteredEndpoints = {};
    Object.keys(endpoints).forEach(key => {
      filteredEndpoints[key] = endpoints[key].slice(startIndex, endIndex);
    });
    
    return { labels: filteredLabels, endpoints: filteredEndpoints };
  };

  const filteredTrendData = getFilteredTrendData();

  const monthlyTrendData = {
    labels: filteredTrendData.labels,
    datasets:
      trendEndpoints
        ?.map((endpointKey) => {
          if (trendVisible[endpointKey] === false) return null;
          const seriesIndex = trendEndpoints.indexOf(endpointKey);
          const color = data.monthlyEndpointTrend.colors?.[endpointKey] || "#667eea";
          return {
            label: endpointKey,
            data: filteredTrendData.endpoints[endpointKey] || [],
            fill: false,
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            borderDash: getTrendLineDash(endpointKey, seriesIndex),
            tension: 0.35,
            pointRadius: 4,
            pointHoverRadius: 7,
            pointHitRadius: 12,
            pointBackgroundColor: "#ffffff",
            pointBorderColor: color,
            pointBorderWidth: 2,
            pointHoverBackgroundColor: "#ffffff",
            pointHoverBorderColor: color,
            pointHoverBorderWidth: 2,
          };
        })
        .filter(Boolean) || [],
  };

  const trendDateRange =
    filteredTrendData.labels.length >= 2
      ? `${filteredTrendData.labels[0]} — ${filteredTrendData.labels[filteredTrendData.labels.length - 1]}`
      : filteredTrendData.labels[0] || "";

  const trendMaxValue = monthlyTrendData.datasets
    .flatMap((dataset) => dataset.data || [])
    .reduce((max, value) => Math.max(max, Number(value) || 0), 0);
  const trendYAxisMax = Math.max(120, Math.ceil(trendMaxValue / 20) * 20);

  const trendChartOptions = {
    maintainAspectRatio: false,
    layout: {
      padding: { top: 6, right: 10, bottom: 2, left: 2 },
    },
    interaction: {
      mode: "index",
      intersect: true,
    },
    animations: {
      radius: {
        duration: 180,
        easing: "easeOutQuad",
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        titleColor: "#94a3b8",
        bodyColor: "#f8fafc",
        borderColor: "rgba(255,255,255,0.08)",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6,
        caretSize: 6,
        caretPadding: 16,
        titleFont: { size: 12, weight: "500" },
        bodyFont: { size: 12 },
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${context.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: {
          color: "rgba(148,163,184,0.2)",
          borderDash: [3, 4],
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        max: trendYAxisMax,
        border: { display: false },
        grid: {
          color: "rgba(148,163,184,0.2)",
          borderDash: [3, 4],
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          stepSize: 20,
        },
      },
    },
  };

  const toggleTrendSeries = (id) => {
    setTrendVisible((prev) => ({
      ...prev,
      [id]: prev[id] === false ? true : false,
    }));
  };

  // Threat Sources Bubble
  const socBarColors = [
    "rgba(111,66,193,0.7)", "rgba(59,130,246,0.7)", "rgba(16,185,129,0.7)",
    "rgba(245,158,11,0.7)", "rgba(239,68,68,0.7)", "rgba(14,165,233,0.7)",
    "rgba(168,85,247,0.7)", "rgba(34,197,94,0.7)", "rgba(251,146,60,0.7)", "rgba(236,72,153,0.7)",
  ];

  const socBarSourceData = threatSources.length > 0
    ? threatSources
    : [
        { source: "CrowdStrike", count: 120 },
        { source: "SentinelOne", count: 85 },
        { source: "Defender", count: 60 },
        { source: "Carbon Black", count: 30 },
      ];

  const socBarData = {
    labels: socBarSourceData.map((s) => s.source),
    datasets: [
      {
        label: "Endpoints",
        data: socBarSourceData.map((s) => s.count),
        backgroundColor: socBarSourceData.map((_, i) => socBarColors[i % socBarColors.length]),
        borderColor: socBarSourceData.map((_, i) => socBarColors[i % socBarColors.length].replace("0.7", "1")),
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const toggleBubbleSeries = (id) => {
    setBubbleVisible((prev) => ({
      ...prev,
      [id]: prev[id] === false ? true : false,
    }));
  };

  // Month-over-Month Change chart (stacked by endpoint type, limited to last 6 months of deltas)
  const momLabels = [];
  const momStackedDatasets = [];
  const hasMomData = (() => {
    if (!data?.monthlyEndpointTrend?.labels || !data?.monthlyEndpointTrend?.endpoints) return false;
    const allTrendLabels = data.monthlyEndpointTrend.labels;
    const allTrendEndpoints = data.monthlyEndpointTrend.endpoints;
    if (allTrendLabels.length < 2) return false;

    // Limit to last 7 months of source data so MoM shows 6 delta bars
    const momStart = Math.max(0, allTrendLabels.length - 7);
    const trendLabels = allTrendLabels.slice(momStart);
    const trendEndpoints = {};
    Object.entries(allTrendEndpoints).forEach(([key, counts]) => {
      trendEndpoints[key] = counts.slice(momStart);
    });

    for (let i = 1; i < trendLabels.length; i++) {
      momLabels.push(trendLabels[i]);
    }

    Object.entries(trendEndpoints).forEach(([endpointKey, counts]) => {
      const deltas = [];
      for (let i = 1; i < trendLabels.length; i++) {
        deltas.push((counts[i] || 0) - (counts[i - 1] || 0));
      }
      const color = data.monthlyEndpointTrend.colors?.[endpointKey] || "#667eea";
      momStackedDatasets.push({
        label: endpointKey,
        data: deltas,
        backgroundColor: color + "BB",
        borderColor: color,
        borderWidth: 1,
        borderRadius: 4,
        stack: "mom",
      });
    });

    return momLabels.length > 0 && momStackedDatasets.some((ds) => ds.data.some((v) => v !== 0));
  })();

  const momData = {
    labels: momLabels,
    datasets: momStackedDatasets,
  };

  // Bar Chart - use detailsData if available (from date filter), otherwise use data.endpointDetails
  // Filter out zero-count endpoints, then sort largest to smallest so chart reads left-to-right naturally
  const barDataSource = detailsData || data?.endpointDetails;
  const barFiltered = (() => {
    const labels = barDataSource?.labels || [];
    const values = barDataSource?.values || [];
    const colors = barDataSource?.colors || [];
    const defaultColors = ["#667eea", "#764ba2", "#43e97b", "#fa709a", "#4facfe", "#30cfd0", "#a8edea", "#f093fb"];
    // Keep only entries with count > 0, then sort descending by value
    const entries = labels
      .map((label, i) => ({ label, value: values[i] || 0, color: colors[i] || defaultColors[i % defaultColors.length] }))
      .filter(e => e.value > 0)
      .sort((a, b) => b.value - a.value);
    return {
      labels: entries.map(e => e.label),
      values: entries.map(e => e.value),
      colors: entries.map(e => e.color),
    };
  })();
  const barData = {
    labels: barFiltered.labels,
    datasets: [
      {
        label: "Endpoints",
        data: barFiltered.values,
        backgroundColor: barFiltered.colors.map((c) => c + "AA"),
        borderColor: barFiltered.colors,
        borderWidth: 2,
        borderRadius: 8,
        // Chart.js: minimum bar height in pixels so tiny values (e.g. 1 vs 450) stay visible; data/tooltips unchanged
        minBarLength: 10,
      },
    ],
  };

  /* -------------------------------------------------------------------------- */
  /*                                  RENDER                                    */
  /* -------------------------------------------------------------------------- */

  const axisTextColor = isDarkMode ? "#ffffff" : "#2c2c2c";

  return (
    <motion.div className="p-3" initial="initial" animate="animate" variants={staggerContainer}>
      {/* Banner */}
      <motion.div 
        variants={fadeUp} 
        className="mb-3"
        style={{ 
          marginTop: isMobile ? "0.5rem" : "-1.5rem"
        }}
      >
        <Card
          style={{
            borderRadius: 12,
            background: (() => {
              const isDark = document.body.classList.contains('dark-mode');
              return isDark 
                ? 'linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(99,102,241,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(99,102,241,0.08) 100%)';
            })(),
            border: "1px solid var(--color-border)",
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    background: "rgba(59,130,246,0.15)",
                    padding: 10,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaShieldAlt size={20} color="#3b82f6" />
                </div>

                <div>
                  <h4 className="fw-bold mb-0" style={{ fontSize: 18, color: "var(--color-text)" }}>
                    Security Dashboard For{clientName && <span style={{ color: "#3b82f6", marginLeft: 8 }}>{clientName}</span>}
                  </h4>
                  <p className="mb-0" style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    Monitor your security endpoints
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                <div
                  style={{
                    background: "rgba(59,130,246,0.1)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaShieldAlt size={14} color="#3b82f6" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#3b82f6" }}>Endpoints</span>
                </div>

                <div
                  style={{
                    background: "rgba(59,130,246,0.1)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaChartLine size={14} color="#3b82f6" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#3b82f6" }}>Trends</span>
                </div>

                <div
                  onClick={() => { setAnalyticsOpen(true); setMomFullscreen(false); }}
                  style={{
                    background: "rgba(59,130,246,0.1)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(59,130,246,0.25)"; e.currentTarget.style.transform = "scale(1.03)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(59,130,246,0.1)"; e.currentTarget.style.transform = "scale(1)"; }}
                >
                  <FaChartBar size={14} color="#3b82f6" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#3b82f6" }}>Analytics</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </motion.div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={6} className={isMobile ? "mb-3" : ""}>
          <motion.div variants={fadeUp}>
            <OverviewCard 
              title={(() => {
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const now = new Date();
                // Calculate previous month
                let prevMonth = now.getMonth() - 1;
                let prevYear = now.getFullYear();
                if (prevMonth < 0) {
                  prevMonth = 11; // December
                  prevYear = prevYear - 1;
                }
                const previousMonthLabel = `${monthNames[prevMonth]} ${prevYear}`;
                return `Total SOC Endpoints in ${previousMonthLabel}`;
              })()}
              value={data.summary?.totalSocEndpoints?.toLocaleString() || 0} 
              icon={FaServer} 
              color="#6f42c1" 
            />
          </motion.div>
        </Col>

        <Col md={6}>
          <motion.div variants={fadeUp}>
            <OverviewCard title="Unique Security Tools" value={(data.summary?.uniqueEndpointTypes || 0).toLocaleString()} icon={FaShieldAlt} color="#3b82f6" />
          </motion.div>
        </Col>
      </Row>

      {/* Donut + Trend */}
      <Row className="mb-4 align-items-stretch">
        {/* Doughnut */}
        <Col md={4} className={`${isMobile ? "mb-3" : ""} d-flex`}>
          <motion.div variants={fadeUp} className="w-100">
            <Card
              className="h-100"
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
              }}
            >
              <Card.Body className="d-flex flex-column h-100">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <HeadingIcon icon={FaCodiepie} color="#f59e0b" bg="rgba(245,158,11,0.15)" />
                    <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>
                      Endpoint Distribution
                    </h6>
                  </div>

                  <LegendDropdown items={endpointLegendItems} open={endpointLegendOpen} onToggleOpen={() => setEndpointLegendOpen((prev) => !prev)} onToggleItem={toggleEndpointLabel} />
                </div>

                <div
                  className="endpoint-dist-wrapper"
                  style={{ position: "relative", flex: "1 1 0", minHeight: SECURITY_CHART_MIN_HEIGHT, outline: "none" }}
                >
                  <style>{`.endpoint-dist-wrapper *:focus, .endpoint-dist-wrapper svg { outline: none !important; }`}</style>
                  {hasEndpointDistributionData ? (
                    <>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={rechartsEndpointData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            startAngle={360}
                            endAngle={0}
                            innerRadius="55%"
                            outerRadius="90%"
                            paddingAngle={2}
                            stroke="none"
                            animationDuration={1200}
                            animationEasing="ease-out"
                          >
                            {rechartsEndpointData.map((entry, index) => (
                              <Cell key={`ep-cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <RechartsTooltip content={<EndpointDistributionTooltip />} />
                          <RechartsLegend content={renderEndpointLegend} verticalAlign="bottom" />
                        </PieChart>
                      </ResponsiveContainer>
                      <div
                        style={{
                          position: "absolute", top: "44%", left: "50%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center", pointerEvents: "none",
                        }}
                      >
                        <div style={{ fontSize: 24, fontWeight: 800, color: "var(--color-text)", lineHeight: 1.1 }}>
                          {endpointDistributionTotal.toLocaleString()}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--color-text-light)", marginTop: 4, lineHeight: 1.3 }}>
                          Total
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        height: SECURITY_CHART_MIN_HEIGHT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--color-text-light)",
                        fontSize: 14,
                      }}
                    >
                      No data available
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        {/* Trend Line */}
        <Col md={8} className="d-flex">
          <motion.div variants={fadeUp} className="w-100">
            <Card
              className="h-100"
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
              }}
            >
              <Card.Body className="d-flex flex-column h-100">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-start gap-2">
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        background: "linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(99,102,241,0.12) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FaChartLine size={15} color="#8b5cf6" />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)", fontSize: 14, lineHeight: 1.3 }}>
                        Monthly Endpoint Trends (SOC)
                      </h6>
                      {trendDateRange && (
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                          {trendDateRange}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
                    <MonthBadge
                      selectedMonth={trendSelectedMonth}
                      setSelectedMonth={setTrendSelectedMonth}
                      showPicker={trendShowPicker}
                      setShowPicker={setTrendShowPicker}
                      pickerRef={trendPickerRef}
                      availableDates={socAvailableDates}
                    />
                    <LegendDropdown
                      items={trendLegendItems}
                      open={trendLegendOpen}
                      onToggleOpen={() => setTrendLegendOpen((prev) => !prev)}
                      onToggleItem={toggleTrendSeries}
                      icon={FaList}
                    />
                  </div>
                </div>

                <TrendInlineLegend items={trendLegendItems} />

                <div
                  style={{ flex: "1 1 0", minHeight: 0, position: "relative" }}
                >
                  <Line data={monthlyTrendData} options={trendChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Bubble + Bar */}
      <Row className="mb-4">
        {/* Bubble */}
        <Col lg={6} className={isMobile ? "mb-3" : ""}>
          <motion.div variants={fadeUp} style={{ height: "100%" }}>
            <Card
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                height: "100%",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <HeadingIcon icon={FaShieldAlt} color="#3b82f6" bg="rgba(59,130,246,0.15)" />
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>
                        Endpoint Month-over-Month Change
                      </h6>
                      {momLabels.length >= 2 && (
                        <span style={{ fontSize: 11, color: "var(--color-text-secondary, #94a3b8)" }}>
                          {momLabels[0]} → {momLabels[momLabels.length - 1]}
                        </span>
                      )}
                    </div>
                  </div>
                  {hasMomData && (
                    <button
                      onClick={() => setMomFullscreen(true)}
                      title="Expand chart"
                      style={{
                        background: "rgba(59,130,246,0.1)",
                        border: "1px solid rgba(59,130,246,0.3)",
                        borderRadius: 8,
                        padding: "6px 10px",
                        cursor: "pointer",
                        color: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                      }}
                    >
                      <FaExpand size={12} />
                      <span>Expand</span>
                    </button>
                  )}
                </div>

                <div style={{ minHeight: "45dvh", position: "relative" }}>
                  {!hasMomData ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "45dvh" }}>
                      <span style={{ color: "var(--color-text-secondary, #94a3b8)", fontSize: 13 }}>
                        Not enough trend data to compute month-over-month change.
                      </span>
                    </div>
                  ) : (
                    <Bar
                      data={momData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: "bottom",
                            labels: {
                              color: axisTextColor,
                              font: { size: 11 },
                              boxWidth: 12,
                              padding: 10,
                            },
                          },
                          tooltip: {
                            backgroundColor: "var(--color-bg-card)",
                            titleColor: axisTextColor,
                            bodyColor: axisTextColor,
                            borderColor: "var(--color-border)",
                            borderWidth: 1,
                            callbacks: {
                              title: (items) => items[0]?.label || "",
                              label: (ctx) => {
                                const delta = ctx.parsed.y;
                                const sign = delta >= 0 ? "+" : "";
                                return ` ${ctx.dataset.label}: ${sign}${delta}`;
                              },
                              footer: (items) => {
                                const net = items.reduce((sum, ctx) => sum + ctx.parsed.y, 0);
                                const sign = net >= 0 ? "+" : "";
                                return `Net: ${sign}${net}`;
                              },
                            },
                          },
                          annotation: {},
                        },
                        scales: {
                          x: {
                            stacked: true,
                            ticks: { color: axisTextColor, font: { size: 11 } },
                            grid: { color: "rgba(148,163,184,0.1)" },
                          },
                          y: {
                            stacked: true,
                            ticks: {
                              color: axisTextColor,
                              font: { size: 11 },
                              callback: (v) => (v >= 0 ? `+${v}` : `${v}`),
                            },
                            grid: { color: "rgba(148,163,184,0.1)" },
                            border: { dash: [4, 4] },
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        {/* MoM Fullscreen Modal */}
        {momFullscreen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(0,0,0,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
            onClick={() => setMomFullscreen(false)}
          >
            <div
              style={{
                background: "var(--color-bg-card)",
                borderRadius: 18,
                width: "95vw",
                height: "90vh",
                display: "flex",
                flexDirection: "column",
                padding: 24,
                boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>Endpoint Month-over-Month Change</h5>
                  {momLabels.length >= 2 && (
                    <span style={{ fontSize: 12, color: "var(--color-text-secondary, #94a3b8)" }}>
                      {momLabels[0]} → {momLabels[momLabels.length - 1]}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setMomFullscreen(false)}
                  style={{
                    background: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    cursor: "pointer",
                    color: "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                  }}
                >
                  <FaCompress size={12} /> Close
                </button>
              </div>
              <div style={{ flex: 1, position: "relative" }}>
                <Bar
                  data={momData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "bottom",
                        labels: { color: axisTextColor, font: { size: 12 }, boxWidth: 14, padding: 14 },
                      },
                      tooltip: {
                        backgroundColor: "var(--color-bg-card)",
                        titleColor: axisTextColor,
                        bodyColor: axisTextColor,
                        borderColor: "var(--color-border)",
                        borderWidth: 1,
                        callbacks: {
                          title: (items) => items[0]?.label || "",
                          label: (ctx) => { const d = ctx.parsed.y; return ` ${ctx.dataset.label}: ${d >= 0 ? "+" : ""}${d}`; },
                          footer: (items) => { const net = items.reduce((s, c) => s + c.parsed.y, 0); return `Net: ${net >= 0 ? "+" : ""}${net}`; },
                        },
                      },
                    },
                    scales: {
                      x: { stacked: true, ticks: { color: axisTextColor, font: { size: 12 } }, grid: { color: "rgba(148,163,184,0.1)" } },
                      y: {
                        stacked: true,
                        ticks: { color: axisTextColor, font: { size: 12 }, callback: (v) => (v >= 0 ? `+${v}` : `${v}`) },
                        grid: { color: "rgba(148,163,184,0.1)" },
                        border: { dash: [4, 4] },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Bar */}
        <Col lg={6}>
          <motion.div variants={fadeUp} style={{ height: "100%" }}>
            <Card
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                height: "100%",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <HeadingIcon icon={FaServer} color="#10b981" bg="rgba(16,185,129,0.15)" />
                    <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>
                      Endpoint Details
                    </h6>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <MonthBadge selectedMonth={detailsSelectedMonth} setSelectedMonth={setDetailsSelectedMonth} showPicker={detailsShowPicker} setShowPicker={setDetailsShowPicker} pickerRef={detailsPickerRef} availableDates={detailsAvailableDates} />
                  </div>
                </div>

                <div style={{ minHeight: "45dvh", display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                  {detailsLoading ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: "var(--color-bg-card)",
                          padding: "10px 18px", borderRadius: 10,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          border: "1px solid var(--color-border)",
                        }}
                      >
                        <FaSpinner className="spin" size={14} color="#8b5cf6" />
                        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text)" }}>Loading...</span>
                      </motion.div>
                    </div>
                  ) : barFiltered.labels.length === 0 ? (
                    <div style={{
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      flex: 1, gap: 10,
                    }}>
                      <span style={{ fontSize: 22 }}>📭</span>
                      <span style={{ fontSize: 13, color: "var(--color-text-light, #94a3b8)", textAlign: "center" }}>
                        No endpoint data available for this month.
                      </span>
                    </div>
                  ) : (
                    <EndpointDetailsChart
                      labels={barFiltered.labels}
                      values={barFiltered.values}
                      colors={barFiltered.colors}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Analytics Modal — extracted to SecurityAnalyticsModal component */}
      <SecurityAnalyticsModal
        open={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        data={data}
        clientName={clientName}
        isMobile={isMobile}
      />

    </motion.div>
  );
};

export default SecurityOverview;